import { useEffect } from 'react';
import { Spinner, Chip } from '@heroui/react';
import { X } from 'lucide-react';
import { useLineItemCost } from '@/api/calculations';
import type { ProjectCostResult } from '@/api/calculations';

interface CostBreakdownPanelProps {
  lineItemId: string | null;
  projectCost: ProjectCostResult | null;
  onClose: () => void;
}

const fmt = (n: number) => `$${n.toFixed(2)}`;
const pct = (n: number) => `${(n * 100).toFixed(0)}%`;
const fmtMinutes = (n: number): string =>
  Math.abs(n - Math.round(n)) < 0.005 ? String(Math.round(n)) : n.toFixed(2);

export default function CostBreakdownPanel({ lineItemId, projectCost, onClose }: CostBreakdownPanelProps) {
  const { data: itemCost, isLoading, isError, refetch } = useLineItemCost(lineItemId ?? undefined);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!lineItemId) return null;

  const itemInfo = projectCost?.areas
    .flatMap((a) => a.line_items)
    .find((li) => li.item_id === lineItemId);

  const laborCost = projectCost?.labor_per_item?.[lineItemId] ?? 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 overflow-y-auto"
        style={{
          width: '380px',
          backgroundColor: 'var(--bg-surface)',
          borderLeft: '1px solid var(--border)',
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <p className="text-sm" style={{ color: '#e74c3c' }}>Failed to load cost breakdown</p>
            <button onClick={() => refetch()} className="text-xs px-3 py-1 rounded" style={{ backgroundColor: '#2a3a4a', color: 'var(--text-primary)' }}>Retry</button>
            <button onClick={onClose} className="text-xs underline" style={{ color: 'var(--text-muted)' }}>Close</button>
          </div>
        ) : itemCost ? (
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {itemInfo?.cabinet_code || 'Item'}
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {itemInfo?.description}
                </div>
              </div>
              <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]">
                <X size={16} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            {/* Summary bar */}
            <div className="flex justify-between rounded-lg px-4 py-3 mb-4" style={{ backgroundColor: '#0d1b2a' }}>
              <div>
                <div className="text-[10px] uppercase" style={{ color: '#888' }}>Qty</div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{itemInfo?.quantity ?? 1}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase" style={{ color: '#888' }}>Cost</div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{fmt(itemCost.total_cost_before_margin)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase" style={{ color: '#888' }}>Price</div>
                <div className="text-sm font-semibold" style={{ color: '#2ecc71' }}>{fmt(itemCost.price ?? itemCost.total_cost_before_margin)}</div>
              </div>
            </div>

            {/* Materials */}
            {itemCost.material_cost_source === 'pending' ? (
              <div className="mb-3">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase pb-1 mb-2" style={{ color: '#3498db', borderBottom: '1px solid #2a3a4a' }}>
                  <span>Materials</span>
                  <Chip size="sm" color="warning" variant="flat">PENDING</Chip>
                </div>
                {itemCost.material_details && itemCost.material_details.length > 0 && (
                  itemCost.material_details.map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-0.5 text-[11px]">
                      <span style={{ color: 'var(--text-secondary)' }}>{m.name}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>
                    </div>
                  ))
                )}
              </div>
            ) : itemCost.material_details && itemCost.material_details.length > 0 && (
              <Section title="Materials" total={itemCost.total_material_cost ?? 0}>
                {itemCost.material_details.map((m, i) => {
                  const yards = (m as any).yards as number | undefined;
                  const sub = yards != null
                    ? `${(Math.round(yards * 100) / 100)} yd`
                    : m.sqft != null
                      ? `${(Math.round(m.sqft * 100) / 100)} sqft`
                      : m.linft != null
                        ? `${(Math.round(m.linft * 100) / 100)} LF`
                        : undefined;
                  const isNested = (m as any).nested === true || m.category === 'Nesting';
                  return (
                    <Row
                      key={i}
                      label={m.name}
                      sublabel={sub}
                      badge={isNested ? 'Nested' : undefined}
                      value={m.cost}
                    />
                  );
                })}
              </Section>
            )}

            {/* Hardware */}
            {itemCost.hardware_details && itemCost.hardware_details.length > 0 && (
              <Section title="Hardware" total={itemCost.hardware_cost}>
                {itemCost.hardware_details.map((h, i) => (
                  <Row key={i} label={`${h.name} ×${h.qty}`} sublabel={`@ ${fmt(h.cost_per)}`} value={h.cost} />
                ))}
              </Section>
            )}

            {/* Finish */}
            {itemCost.finish_cost > 0 && (
              <Section title="Finish" total={itemCost.finish_cost}>
                <Row
                  label={itemCost.finish_name ?? 'Finish'}
                  sublabel={itemCost.finish_sqft && itemCost.finish_rate_per_sqft
                    ? `${itemCost.finish_sqft} sqft @ ${fmt(itemCost.finish_rate_per_sqft)}`
                    : undefined}
                  value={itemCost.finish_cost}
                />
              </Section>
            )}

            {/* Labor */}
            {itemCost.exclude_labor ? (
              <div className="mb-3">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase pb-1 mb-2" style={{ color: '#888', borderBottom: '1px solid #2a3a4a' }}>
                  <span>Labor</span>
                  <span>Excluded</span>
                </div>
                <p className="text-[10px] italic" style={{ color: 'var(--text-muted)' }}>
                  Labor has been excluded for this line item.
                </p>
              </div>
            ) : (itemCost.labor_details && itemCost.labor_details.length > 0) ? (
              <Section title="Labor" total={itemCost.labor_cost}>
                {itemCost.labor_details.map((l, i) => {
                  const breakdown = (l.score_breakdown ?? []).filter((r) => r.minutes > 0);
                  const totals = breakdown.reduce(
                    (acc, r) => ({ minutes: acc.minutes + r.minutes, cost: acc.cost + r.cost }),
                    { minutes: 0, cost: 0 },
                  );
                  return (
                    <div key={i}>
                      <Row
                        label={l.rate_name}
                        sublabel={
                          l.unit === 'per sq ft'
                            ? `${l.units.toFixed(1)} ${l.unit} @ ${fmt(l.rate_per_unit)}`
                            : `${Math.round(l.units)} ${l.unit} @ ${fmt(l.rate_per_unit)}`
                        }
                        value={l.cost}
                      />
                      {breakdown.length > 0 && (
                        <div className="ml-3 mt-0.5 mb-1 pl-2 border-l border-[var(--border)]">
                          {breakdown.map((row, j) => (
                            <div
                              key={j}
                              className="flex items-center justify-between text-[10px]"
                              style={{ color: 'var(--text-muted)' }}
                            >
                              <span>
                                {row.label}{' '}
                                <span className="tabular-nums">({fmtMinutes(row.minutes)} min)</span>
                              </span>
                              <span className="tabular-nums">{fmt(row.cost)}</span>
                            </div>
                          ))}
                          <div
                            className="flex items-center justify-between text-[10px] font-semibold mt-0.5 pt-0.5 border-t border-[var(--border)]"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <span>
                              Complexity Score{' '}
                              <span className="tabular-nums">({fmtMinutes(totals.minutes)} min)</span>
                            </span>
                            <span className="tabular-nums">{fmt(totals.cost)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Section>
            ) : laborCost > 0 ? (
              <Section title="Labor" total={laborCost}>
                <Row label="Labor" value={laborCost} />
              </Section>
            ) : null}

            {/* Margin Breakdown */}
            {itemCost.margin_breakdown && itemCost.margin_breakdown.length > 0 && (
              <Section title="Margin Breakdown" total={itemCost.margin_breakdown.reduce((s, m) => s + (m.margin_amount || 0), 0)}>
                {itemCost.margin_breakdown.map((m, i) => (
                  <Row key={i} label={`${m.category} (${pct(m.margin_rate)})`} value={m.margin_amount} />
                ))}
              </Section>
            )}

            {/* Unit Price */}
            <div className="flex items-center justify-between pt-3 mt-3 font-bold" style={{ borderTop: '2px solid #2ecc71' }}>
              <span style={{ color: 'var(--text-primary)' }}>UNIT PRICE</span>
              <span className="text-base" style={{ color: '#2ecc71' }}>{fmt(itemCost.price ?? itemCost.total_cost_before_margin)}</span>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

function Section({ title, total, children }: { title: string; total?: number; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase pb-1 mb-2" style={{ color: '#3498db', borderBottom: '1px solid #2a3a4a' }}>
        <span>{title}</span>
        {total !== undefined && <span>{fmt(total)}</span>}
      </div>
      {children}
    </div>
  );
}

function Row({ label, sublabel, badge, value }: { label: string; sublabel?: string; badge?: string; value: number }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-[11px]">
      <div>
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        {sublabel && (
          <span className="ml-1" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>· {sublabel}</span>
        )}
        {badge && (
          <span
            className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase"
            style={{ backgroundColor: 'rgba(52, 152, 219, 0.15)', color: '#3498db' }}
          >
            {badge}
          </span>
        )}
      </div>
      <span style={{ color: 'var(--text-primary)' }}>{fmt(value)}</span>
    </div>
  );
}
